import { Tooltip, Typography } from '@mui/material';
import { Metric } from '@strength/domain';
import {
  bowlingSummaryDayKey,
  type BowlingSummaryDayMap,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { DateTime } from 'luxon';
import { CustomSlot } from '../../../../components/sheet/slots';
import { cn } from '../../../../components/utils';
import { getLocale } from '../../../../utils/i18n';
import { WorkoutWithCycleAndSession } from '../../types';
import { DayCornerIndicator } from '../DayCornerIndicator';
import { resolveSlotWorkout } from '../cross-block-resolver';
import {
  ActualWorkout,
  BlockSummarySelectionData,
  BlockSummarySlot,
  WorkoutSummaryActualsMutationFns,
  WorkoutSummaryPrescriptionMutationFns,
} from '../types';
import { EmptySlot, getSlotsForMonth, MONTH_DAY_COUNT } from './common';
import { getActualSlots, getPlannedSlots } from './getWorkoutSlots';
import { ProvenanceTooltipLines } from './ProvenanceTooltipLines';
import { getLatestPrescriptionProvenance } from './workout-metric-utils';

/**
 * Cross-block overlay context for the day-header indicator. Optional —
 * when omitted, headers render exactly as before with no purple-dot chrome.
 */
export type BlockSummaryHeaderOptions = {
  /** Per-day annotation map built from `buildBowlingSummaryDays`. */
  crossBlockOverlay: BowlingSummaryDayMap;
  /** Athlete whose row this view represents — used to key into the map. */
  profileId: string | null;
  /** Locale for tooltip date formatting. */
  locale: string;
  /** This athlete's prescription workouts, for per-day provenance lookup. */
  prescriptionWorkouts: WorkoutWithCycleAndSession[];
};

export const getBlockSummarySlotsForMonth = (
  month: number,
  year: number,
  prescriptionWorkouts: WorkoutWithCycleAndSession[],
  actualsWorkouts: ActualWorkout[],
  prescriptionMutationFns: WorkoutSummaryPrescriptionMutationFns,
  actualsMutationFns: WorkoutSummaryActualsMutationFns,
  getMetric: (metricId: string) => Metric,
  t: I18nTranslator,
  readOnly?: boolean,
  headerOptions?: BlockSummaryHeaderOptions,
): {
  slots: BlockSummarySlot[];
  rowCount: number;
  columnCount: number;
} => {
  const slots: BlockSummarySlot[] = [];

  let currentX = 0;
  let currentY = 0;

  // Spacer column
  slots.push(EmptySlot(currentX++, currentY));

  // For each day in the month, add a slot for each day
  slots.push(
    ...getSlotsForMonth(
      currentX,
      currentY,
      month,
      year,
      (x, y, day) => HeaderDaySlot(x, y, month, day, year, t, headerOptions),
      true,
    ),
  );
  currentX += MONTH_DAY_COUNT;
  currentY++;

  // Add row for Planned section header
  slots.push(
    new CustomSlot<BlockSummarySelectionData>(
      { x: 0, y: currentY++ },
      MONTH_DAY_COUNT + 1,
      1,
      false,
      () => <Typography variant="subtitle1Medium">{t('planned')}</Typography>,
      { className: 'bg-gray-100 p-2', disableContextMenu: true },
    ),
  );

  // Get slots for planned section
  slots.push(
    ...getPlannedSlots(
      prescriptionWorkouts,
      month,
      year,
      currentY,
      prescriptionMutationFns,
      getMetric,
      t,
      readOnly,
    ),
  );

  // Add 3 rows: 1 for Match, 1 for Training, and 1 for Modified
  currentY += 3;

  // Add row for Actuals section header
  slots.push(
    new CustomSlot<BlockSummarySelectionData>(
      { x: 0, y: currentY++ },
      MONTH_DAY_COUNT + 1,
      1,
      false,
      () => <Typography variant="subtitle1Medium">{t('actuals')}</Typography>,
      {
        className: 'bg-gray-100 p-2',
        disableContextMenu: true,
      },
    ),
  );

  // Get slots for actuals section
  slots.push(
    ...getActualSlots(
      actualsWorkouts,
      actualsMutationFns,
      month,
      year,
      currentY,
      getMetric,
      t,
      readOnly,
    ),
  );

  // Add 3 rows: 1 for Match, 1 for Training, and 1 for Modified
  currentY += 3;

  return { slots, rowCount: currentY, columnCount: currentX };
};

const HeaderDaySlot = (
  x: number,
  y: number,
  month: number,
  day: number,
  year: number,
  t: I18nTranslator,
  headerOptions?: BlockSummaryHeaderOptions,
) => {
  const dt = DateTime.fromObject({ month, day, year });
  const locale = headerOptions?.locale ?? getLocale();
  const dayOfWeekNameShort = dt.toFormat('EEE', { locale });
  const isToday = dt.toISODate() === DateTime.now().toISODate();

  // Look up the cross-block annotation for this day. Skipped entirely when
  // the caller did not pass an overlay (e.g. earlier callers / tests).
  const assignedDate = dt.toISODate();
  const profileId = headerOptions?.profileId ?? null;
  const crossBlock =
    headerOptions && profileId && assignedDate
      ? headerOptions.crossBlockOverlay.get(
          bowlingSummaryDayKey(profileId, assignedDate),
        )
      : undefined;
  const showIndicator = !!crossBlock && crossBlock.kind !== 'current';

  // Prescription provenance for this day: provenance of the most recent
  // prescription value across the day's planned rows. No values → no hover.
  const dayPrescriptionWorkouts = assignedDate
    ? (headerOptions?.prescriptionWorkouts ?? []).filter(
        (w) => w.assignedDate === assignedDate,
      )
    : [];
  // Resolve each workout through the cross-block overlay first — on overlay
  // days the planned cell renders another block's workout, and the header
  // hover must show that workout's provenance, not the current block's.
  // All of the day's workouts contribute: the helper picks the latest
  // enteredOn, so same-day workouts cannot shadow each other by position.
  const renderedWorkouts = dayPrescriptionWorkouts.map(
    (w) => resolveSlotWorkout(w, w.crossBlock).workout,
  );
  const provenance = getLatestPrescriptionProvenance(renderedWorkouts);
  const hasProvenance = !!(provenance.enteredBy || provenance.enteredOn);

  // Cross-team annotation for the hover: the source team's name, shown under
  // the date as "Team Entered: <team>". Same-team overlays show nothing.
  const crossTeamName =
    crossBlock && crossBlock.kind !== 'current'
      ? crossBlock.kind === 'duplicate'
        ? crossBlock.primary.isCrossTeam
          ? crossBlock.primary.teamName
          : undefined
        : crossBlock.isCrossTeam
          ? crossBlock.source.teamName
          : undefined
      : undefined;

  const dayLabel = (
    <>
      <Typography variant="caption">{dayOfWeekNameShort}</Typography>
      <Typography
        variant="caption"
        className={cn(
          isToday && 'bg-blue-600 text-white rounded-full px-[2.5px]',
        )}
      >
        {day}
      </Typography>
      {showIndicator && assignedDate && (
        <DayCornerIndicator crossBlock={crossBlock} />
      )}
    </>
  );

  // Cross-team days must show the popover even when the source prescription
  // carries no provenance (never practitioner-edited) — the Team Entered
  // line alone is worth showing.
  if (!hasProvenance && !crossTeamName) {
    return new CustomSlot<BlockSummarySelectionData>(
      { x, y },
      1,
      1,
      false,
      () => dayLabel,
      {
        className:
          'flex flex-col w-[36px] items-center justify-center relative',
        disableContextMenu: true,
      },
    );
  }

  return new CustomSlot<BlockSummarySelectionData>(
    { x, y },
    1,
    1,
    false,
    () => (
      <Tooltip
        placement="bottom"
        arrow
        title={
          <ProvenanceTooltipLines
            headerDate={assignedDate ?? undefined}
            enteredBy={provenance.enteredBy}
            enteredOn={provenance.enteredOn}
            // Deliberate copy: the planned header shows "Last Updated By/On"
            // rather than "Entered" — a generic term agreed with product,
            // since a day's prescription can be edited by several people.
            labelKeys={{ by: 'lastUpdatedBy', on: 'lastUpdatedOn' }}
            sourceLine={
              crossTeamName
                ? { label: t('teamEntered'), value: crossTeamName }
                : undefined
            }
            t={t}
          />
        }
      >
        <span className="flex w-full cursor-default flex-col items-center justify-center">
          {dayLabel}
        </span>
      </Tooltip>
    ),
    {
      className: 'flex flex-col w-[36px] items-center justify-center relative',
      disableContextMenu: true,
    },
  );
};
